import { FormValidate, ToastStatusGlobal } from '@/components';
import { BodyForm, FieldRules } from '@/components/formValidate';
import { FormValidateHandler } from '@/components/formValidate/types/formValidate.type';
import { maxLength } from '@/components/formValidate/validates/maxLength';
import ShowHide from '@/components/showHide';
import { BeneficiaryInfo } from '@/modules/beneficiary/components';
import { FormFields } from '@/modules/move/components/dialogRowEdition/components/formFields';
import { checkIfValueIsSomeWithError } from '@/modules/move/components/dialogRowEdition/utils/checkIfValueIsSomeWithError';
import { requiredIf } from '@/modules/move/components/dialogRowEdition/utils/requiredIf';
import { transformRowToForm } from '@/modules/move/components/dialogRowEdition/utils/transformRowToForm';
import { MoveSheetContent, MoveSheetError, MoveSheetNormalized } from '@/modules/move/entities';
import { MOVE_SHEET_COLUMN } from '@/modules/move/entities/constants/moveSheetColumn';
import {
  MOVE_SHEET_OWNERSHIP,
  MOVE_SHEET_OWNERSHIP_TRANSLATE,
} from '@/modules/move/entities/constants/moveSheetValues';
import { MOVE_ROW_PROCESS_STATUS } from '@/modules/move/entities/enums/move';
import { EditedRow } from '@/modules/move/hooks/useMoveEdition/useMoveEdition';
import MoveSheetService from '@/modules/move/services/moveSheetService';
import { KINSHIP_DEGREE } from '@/pages/moves/shared/constants';
import ICONS from '@/shared/entities/constants/icons';
import { tryOrCatchMessageError } from '@/utils/tryOrCatchMessageError';
import React, { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { Button, Checkbox, Dialog, Text } from 'vkit/lib/components';
import { Grid } from 'vkit/lib/context';

interface DialogRowEditionProps {
  setRowToEdition: Dispatch<SetStateAction<MoveSheetContent | null>>;
  rowToEdition: MoveSheetContent | null;
  originalRow: MoveSheetNormalized | null;
  rowError: MoveSheetError;
  setRowError: Dispatch<SetStateAction<MoveSheetError>>;
  updateRow: (line: string, row: Partial<EditedRow>) => void;
  editedRow?: EditedRow;
  editedLineNumbers?: string[];
  onlyFieldWithError?: boolean;
  line: string;
}

export const DialogRowEdition: React.FC<DialogRowEditionProps> = ({
  rowToEdition,
  updateRow,
  originalRow,
  setRowToEdition,
  rowError,
  editedRow,
  onlyFieldWithError,
  line,
  editedLineNumbers,
  setRowError,
}) => {
  const useRefDialog = useRef({ open: () => {}, dismiss: () => {} });
  const formRef = useRef<FormValidateHandler>(null);
  const { toast } = ToastStatusGlobal();
  const [formData, setFormData] = useState<MoveSheetContent | null>(null);
  const [isLifeActive, setIsLifeActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasBankAccount, setHasBankAccount] = useState(true);

  const getBody = useCallback(
    ({ useErrors, useValues, onChange, onBlur }: BodyForm) =>
      formData && (
        <FormFields
          useErrors={useErrors}
          useValues={useValues}
          onChange={onChange}
          onBlur={onBlur}
          onlyFieldWithError={onlyFieldWithError}
          rowError={rowError}
          rowToEdition={rowToEdition}
          setFormData={setFormData}
          originalRow={originalRow}
          hasBankAccount={hasBankAccount}
          isLifeActive={isLifeActive}
          setHasBankAccount={setHasBankAccount}
          setRowError={setRowError}
        />
      ),
    [
      formData,
      hasBankAccount,
      isLifeActive,
      onlyFieldWithError,
      originalRow,
      rowError,
      rowToEdition,
      setRowError,
    ],
  );

  const handleSubmit = useCallback(
    async (currentRow: Partial<EditedRow>) => {
      await tryOrCatchMessageError(
        async () => {
          if (!currentRow.fields?.matricula || !originalRow?.fileId) {
            throw new Error('Não foi possível salvar as alterações, tente novamente mais tarde.');
          }

          const { data: familyGroupRows } = await new MoveSheetService().getMoveSheet({
            registration: String(currentRow.fields.matricula),
            fileId: originalRow.fileId,
          });

          for (const memberRow of familyGroupRows) {
            if (memberRow.hasError || editedLineNumbers?.includes(String(memberRow.line))) {
              continue;
            }

            updateRow(String(memberRow.line), {
              fields: memberRow.originalContent,
              status: currentRow.status,
              accountable: false,
            });
          }

          currentRow.accountable = true;
          updateRow(line, currentRow);
          setRowToEdition(null);
        },
        {
          tryBefore: () => {
            setLoading(true);
          },
          tryAfter: () => {
            setLoading(false);
          },
          messageErrorDefault: 'Não foi possível salvar as alterações, tente novamente mais tarde.',
        },
      );
    },
    [editedLineNumbers, line, originalRow, setRowToEdition, updateRow],
  );

  const requiredBankAccount = useCallback(() => {
    return (value: string) => {
      if (!value && hasBankAccount) {
        return 'Campo obrigatório';
      }

      return null;
    };
  }, [hasBankAccount]);

  const getRules = useCallback((): FieldRules => {
    const rules: FieldRules = {
      [MOVE_SHEET_COLUMN.contrato]: ['required'],
      [MOVE_SHEET_COLUMN.subfatura]: ['required'],
      [MOVE_SHEET_COLUMN.nome]: ['required'],
      [MOVE_SHEET_COLUMN.dataNascimento]: ['required'],
      [MOVE_SHEET_COLUMN.matricula]: ['required'],
      [MOVE_SHEET_COLUMN.cpf]: [requiredIf('skipValidationCPF', [false, undefined], formData)],
      [MOVE_SHEET_COLUMN.cpfTitular]: [
        requiredIf(MOVE_SHEET_COLUMN.titularidade, [MOVE_SHEET_OWNERSHIP.DEPENDENT], formData),
      ],
      [MOVE_SHEET_COLUMN.sexo]: ['required'],
      [MOVE_SHEET_COLUMN.estadoCivil]: ['required'],
      [MOVE_SHEET_COLUMN.dataInicioVigencia]: ['required'],
      [MOVE_SHEET_COLUMN.nomeMae]: ['required'],
      [MOVE_SHEET_COLUMN.cep]: [
        requiredIf(MOVE_SHEET_COLUMN.titularidade, [MOVE_SHEET_OWNERSHIP.HOLDER], formData),
      ],
      [MOVE_SHEET_COLUMN.uf]: [
        requiredIf(MOVE_SHEET_COLUMN.titularidade, [MOVE_SHEET_OWNERSHIP.HOLDER], formData),
      ],
      [MOVE_SHEET_COLUMN.logradouro]: [
        requiredIf(MOVE_SHEET_COLUMN.titularidade, [MOVE_SHEET_OWNERSHIP.HOLDER], formData),
        maxLength(35),
      ],
      [MOVE_SHEET_COLUMN.bairro]: [
        requiredIf(MOVE_SHEET_COLUMN.titularidade, [MOVE_SHEET_OWNERSHIP.HOLDER], formData),
        maxLength(25),
      ],
      [MOVE_SHEET_COLUMN.cidade]: [
        requiredIf(MOVE_SHEET_COLUMN.titularidade, [MOVE_SHEET_OWNERSHIP.HOLDER], formData),
        maxLength(25),
      ],
      [MOVE_SHEET_COLUMN.complemento]: [],
      [MOVE_SHEET_COLUMN.numero]: [],
      [MOVE_SHEET_COLUMN.plano]: ['required'],
      [MOVE_SHEET_COLUMN.emailSegurado]: [
        requiredIf(MOVE_SHEET_COLUMN.titularidade, [MOVE_SHEET_OWNERSHIP.HOLDER], formData),
        'email',
      ],
      [MOVE_SHEET_COLUMN.grauParentesco]: [
        requiredIf(MOVE_SHEET_COLUMN.titularidade, [MOVE_SHEET_OWNERSHIP.DEPENDENT], formData),
      ],
      [MOVE_SHEET_COLUMN.cns]: [],
      [MOVE_SHEET_COLUMN.dataAdmissao]: [
        requiredIf(MOVE_SHEET_COLUMN.titularidade, [MOVE_SHEET_OWNERSHIP.HOLDER], formData),
      ],
      [MOVE_SHEET_COLUMN.codAngariador]: [],
      [MOVE_SHEET_COLUMN.dataAngariacao]: [],
      [MOVE_SHEET_COLUMN.dataCasamento]: [],
      [MOVE_SHEET_COLUMN.codElegibilidade]: [
        requiredIf(MOVE_SHEET_COLUMN.grauParentesco, [KINSHIP_DEGREE.SPOUSE], formData),
      ],
      [MOVE_SHEET_COLUMN.tipoContaBancaria]: [requiredBankAccount()],
      [MOVE_SHEET_COLUMN.creditoBanco]: [requiredBankAccount()],
      [MOVE_SHEET_COLUMN.creditoNumAgencia]: [requiredBankAccount()],
      [MOVE_SHEET_COLUMN.creditoDVAgencia]: [requiredBankAccount()],
      [MOVE_SHEET_COLUMN.creditoNumConta]: [requiredBankAccount()],
      [MOVE_SHEET_COLUMN.creditoDVConta]: [requiredBankAccount()],
      skipValidationCPF: [],
    };

    return Object.keys(rules).reduce((acc: FieldRules, ruleKey) => {
      acc[ruleKey]?.push(checkIfValueIsSomeWithError(ruleKey as MOVE_SHEET_COLUMN, originalRow));

      return acc;
    }, rules);
  }, [formData, originalRow, requiredBankAccount]);

  useEffect(() => {
    rowToEdition ? useRefDialog.current.open() : useRefDialog.current.dismiss();
    setFormData(transformRowToForm(rowToEdition));
    setIsLifeActive(
      rowToEdition?.[MOVE_SHEET_COLUMN.statusBeneficiario] ===
        MOVE_ROW_PROCESS_STATUS.WAITING_HEALTH_CARD,
    );
  }, [rowToEdition, editedRow, originalRow?.originalContent.statusBeneficiario]);

  return (
    <Dialog
      shadow
      outlined
      rounded
      instanceEmitter={useRefDialog.current}
      elevation={2}
      size={Math.min(1200, window.innerWidth - 100)}
      onClose={() => setRowToEdition(null)}
      head={
        originalRow ? (
          <Grid alignContent='justify' alignItems='bottom'>
            <BeneficiaryInfo
              isHolder={originalRow.originalContent.titularidade === MOVE_SHEET_OWNERSHIP.HOLDER}
              ownership={
                originalRow.originalContent.titularidade === MOVE_SHEET_OWNERSHIP.HOLDER
                  ? MOVE_SHEET_OWNERSHIP_TRANSLATE[MOVE_SHEET_OWNERSHIP.HOLDER]
                  : MOVE_SHEET_OWNERSHIP_TRANSLATE[MOVE_SHEET_OWNERSHIP.DEPENDENT]
              }
              name={String(originalRow.originalContent.nome)}
            />

            <Checkbox
              small
              label='Vida já esta ativa na operadora'
              checked={isLifeActive}
              onChange={setIsLifeActive}
              textLeft
            />
          </Grid>
        ) : undefined
      }
      body={
        <Grid column gap={16}>
          <ShowHide transition='slideToDown' visible={isLifeActive}>
            <Text
              size='medium'
              icon={ICONS.WARNING}
              color='warning'
              iconColor='warning'
              applyBaseColor
              rounded
              padding='8px 12px'
              value='Ao informar que a vida já está ativa a movimentação não será realizada e o Portal RH apenas fará a busca da carteirinha.'
            />
          </ShowHide>

          <FormValidate
            errors={editedRow || isLifeActive ? {} : rowError}
            ref={formRef}
            resource={formData}
            fields={isLifeActive ? {} : getRules()}
            onChangeForm={setFormData}
            onError={() =>
              toast('Puxa!', 'Existem alguns campos com preenchimento irregular.', 'warning')
            }
            onSubmit={async () => {
              if (formData) {
                formData[MOVE_SHEET_COLUMN.statusBeneficiario] = isLifeActive
                  ? MOVE_ROW_PROCESS_STATUS.WAITING_HEALTH_CARD
                  : undefined;

                await handleSubmit({
                  fields: formData,
                });
                setIsLifeActive(false);
              }
            }}
            body={getBody}
          />
        </Grid>
      }
      footer={
        <Grid alignContent='right' gap={8}>
          <Button label='Cancelar' solo outlined onClick={() => setRowToEdition(null)} />
          <Button
            label='Continuar'
            outlined
            loading={loading}
            onClick={async () => {
              formRef.current?.submit();
            }}
            testId='continue'
          />
        </Grid>
      }
    />
  );
};
